import {DateTimeFormat} from '@hconnect/common/components/dateTime/DateTimeFormat'
import {Frequency, LastResult} from '@hconnect/common/types'
import {dataTestId, customThemeConstants, LoadingSwitcher} from '@hconnect/uikit'
import {Box, Button, Stack, Tooltip, Typography, alpha} from '@mui/material'
import i18next, {TFunction} from 'i18next'
import {Moment} from 'moment-timezone'
import React, {useCallback, useEffect, useRef} from 'react'
import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
  useFormContext,
  type PathValue,
  type RegisterOptions
} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {FunctionDefinition} from '../../types'
import {isValueCorrectNumber, isValueError} from '../../utils/lastResult'

import {FormulaFormField} from './FormulaFormField'
import {KpiLastResult} from './KpiLastResult'

import {ErrorKey} from '@settings/modules/common/types'

const MATHEMATICAL_OPERATORS = ['+', '-', '*', '/', '(', ')', '<', '>', '=']

const getErrorMessage = (value?: string | number) => {
  if (i18next.exists(`kpiCalculationSettings.error.${value}`)) {
    return `kpiCalculationSettings.error.${value}`
  }

  return 'kpiCalculationSettings.error.generic'
}

const getTooltipTitle = (t: TFunction, lastResult?: LastResult): React.ReactNode => {
  if (isValueCorrectNumber(lastResult)) {
    return (
      <Stack display="flex" flexWrap="nowrap" gap={0.5}>
        <Typography>{t('kpiCalculationSettings.label.from')}:</Typography>
        <DateTimeFormat
          date={lastResult?.startTime}
          timezone={Intl.DateTimeFormat().resolvedOptions().timeZone}
        />
        <Typography>{t('kpiCalculationSettings.label.to')}:</Typography>
        <DateTimeFormat
          date={lastResult?.endTime}
          timezone={Intl.DateTimeFormat().resolvedOptions().timeZone}
        />
      </Stack>
    )
  }
  if (isValueError(lastResult)) {
    return <Typography>{t(getErrorMessage(lastResult?.value))}</Typography>
  }
  return ''
}

export type Config<TFormValues extends FieldValues> = {
  name: Path<TFormValues>
  rules?: Omit<
    RegisterOptions<TFormValues, Path<TFormValues>>,
    'disabled' | 'setValueAs' | 'valueAsNumber' | 'valueAsDate'
  >
}

type FormulaFormFieldsProps<TFormValues extends FieldValues> = {
  control: Control<TFormValues>
  config: Config<TFormValues>
  showSubmit?: boolean
  frequency?: Frequency
  calculatedValue?: string | null
  isLoading?: boolean
  functions?: FunctionDefinition[]
  lastResult?: LastResult
  setTimeRange?: React.Dispatch<React.SetStateAction<[Moment, Moment] | undefined>>
  onRegisterInsertHandler?: (handler: (fn: string) => void) => void
  formattedTimeRange?: string | undefined
}

export const FormulaFormFields = <TFormValues extends FieldValues>({
  lastResult,
  functions,
  calculatedValue,
  isLoading,
  control,
  config,
  showSubmit,
  onRegisterInsertHandler,
  formattedTimeRange
}: FormulaFormFieldsProps<TFormValues>) => {
  const {
    t,
    i18n: {language}
  } = useTranslation()
  const {getValues, setValue} = useFormContext<TFormValues>()

  const inputRef = useRef<HTMLTextAreaElement | null>(null)

  const insertAtCaret = useCallback(
    (token: string) => {
      const input = inputRef.current
      const current = getValues(config.name) || ''
      if (!input) {
        const newValue = `${current}${token}`
        setValue(config.name, newValue as PathValue<TFormValues, Path<TFormValues>>, {
          shouldDirty: true
        })
        return
      }

      const start = input.selectionStart ?? current.length
      const end = input.selectionEnd ?? current.length

      const newValue = current.slice(0, start) + token + current.slice(end)
      setValue(config.name, newValue as PathValue<TFormValues, Path<TFormValues>>, {
        shouldDirty: true
      })

      requestAnimationFrame(() => {
        input.focus()
        const pos = start + token.length
        input.selectionStart = input.selectionEnd = pos
      })
    },
    [config, getValues, setValue]
  )

  useEffect(() => {
    if (!onRegisterInsertHandler) return
    onRegisterInsertHandler(insertAtCaret)
  }, [onRegisterInsertHandler, getValues, setValue, insertAtCaret])

  return (
    <Box
      {...dataTestId('edit_kpi_form')}
      sx={{
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Controller
        control={control}
        name={config.name}
        rules={config.rules}
        render={({field: {ref, value, onChange}, fieldState: {error}}) => (
          <Box sx={{position: 'relative', minHeight: 10}}>
            <FormulaFormField
              value={value ?? ''}
              ref={(el: HTMLTextAreaElement | null) => {
                ref(el)
                inputRef.current = el
              }}
              onChange={onChange}
              functions={functions}
              error={error}
            />
          </Box>
        )}
      />
      <Box
        sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end'}}
        mt={showSubmit ? 2 : 3.75}
      >
        <Box sx={{display: 'flex', flexWrap: {xs: 'wrap', md: 'no-wrap'}}} gap={1.5}>
          {MATHEMATICAL_OPERATORS.map((operator) => (
            <Button
              key={operator}
              sx={(theme) => ({
                width: 40,
                minWidth: 40,
                height: 40,
                boxShadow: 2,
                borderColor: alpha(theme.palette.secondary.dark, 0.1)
              })}
              color="secondary"
              {...dataTestId(`custom-formula-operator-${operator}`)}
            >
              {operator}
            </Button>
          ))}
        </Box>
        {showSubmit && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              flexDirection: 'row',
              justifyContent: 'flex-end'
            }}
          >
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
              <Typography pr={1} variant="caption">
                {formattedTimeRange}
              </Typography>
              <Tooltip
                title={getTooltipTitle(t, lastResult)}
                placement="top"
                {...dataTestId('last_result_time_range')}
              >
                <Box
                  sx={{
                    backgroundColor: customThemeConstants().palette.contentHover,
                    padding: 1.25,
                    marginRight: 1,
                    border: `1px solid ${customThemeConstants().palette.contentSelectedHover}`,
                    borderRadius: 1
                  }}
                >
                  <LoadingSwitcher isLoading={!!isLoading}>
                    <Box sx={{position: 'relative', minHeight: 10}}>
                      <Typography
                        variant="h4"
                        sx={{
                          color: 'primary.main'
                        }}
                        {...dataTestId('custom_formula_result')}
                      >
                        {calculatedValue ? (
                          calculatedValue ===
                          String(ErrorKey.ERR_UNDEFINED_VARIABLE_OR_CONSTANT) ? (
                            <span>{t('error.undefinedVariableOrConstant')} </span>
                          ) : (
                            <span>= {calculatedValue}</span>
                          )
                        ) : (
                          <>
                            <KpiLastResult
                              language={language}
                              lastResult={lastResult}
                              prefix="= "
                            />
                          </>
                        )}
                      </Typography>
                    </Box>
                  </LoadingSwitcher>
                </Box>
              </Tooltip>
            </Box>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              {...dataTestId('formula-form-verify')}
            >
              {t('kpiCalculationSettings.action.verify')}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}
